import { useEffect } from 'react';

/**
 * useNoIndex — declare a route as off-limits for search-engine indexing.
 *
 * SportSweeps's app shell sets the default `<meta name="robots" content="index, follow">`
 * in /public/index.html so the marketing site indexes cleanly. Specific
 * routes (TV leaderboards, player join flows, anything keyed off a private
 * pool access code) must override that default so they never appear in
 * Google / Bing results — those pages can leak player names, scores, and
 * society identifiers.
 *
 * Usage:
 *   function TVLeaderboardPage() {
 *     useNoIndex();
 *     ...
 *   }
 *
 * Implementation:
 *   • Mounts a `<meta name="robots" content="noindex, nofollow, noarchive">`
 *     tag into <head>, overriding the default site-wide tag.
 *   • Also sets `googlebot` + `bingbot` UA-specific copies for defence-in-depth
 *     (some scrapers honour those over the generic one).
 *   • Restores the original tag on unmount so navigating BACK to an
 *     indexable page (e.g. landing) doesn't leave the site no-indexed.
 *
 * Note: belt-and-braces — backend also sends `X-Robots-Tag: noindex` on the
 * associated API endpoints, so even if a bot caches the API response it
 * won't be indexed.
 */
export function useNoIndex() {
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const originalContent = existing?.getAttribute('content') || '';

    const upsert = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
      return tag;
    };

    upsert('robots', 'noindex, nofollow, noarchive, noimageindex');
    upsert('googlebot', 'noindex, nofollow, noarchive, noimageindex');
    upsert('bingbot', 'noindex, nofollow, noarchive, noimageindex');

    return () => {
      // Restore the site-wide indexable default
      if (existing) {
        existing.setAttribute('content', originalContent || 'index, follow');
      }
      // Remove the bot-specific tags entirely; the marketing site doesn't
      // need them — only no-index routes should bear them.
      document.querySelector('meta[name="googlebot"]')?.remove();
      document.querySelector('meta[name="bingbot"]')?.remove();
    };
  }, []);
}
